import type { IGeoViewColors, IGeoViewFontSizes } from '@/ui/style/types';
import { GeoViewColorClass } from '@/ui/style/types';

/** Default font family stack for GeoView UI components */
export const font = "'Roboto', 'Helvetica', 'Arial', sans-serif";

/** Default base font size multiplier in rem units */
export const defaultFontSize = 1;

/** Canonical typography sizes in design pixels, converted through the owning theme's pxToRem. */
export const TYPOGRAPHY_SCALE = {
  xs: 12,
  sm: 14,
  default: 16,
  md: 18,
  lg: 20,
  xl: 24,
  xxl: 32,
  display: 48,
} as const;

/**
 * Named border-radius tokens, spread onto `theme.shape`.
 *
 * Values are px strings so they render identically in `sx`, `styleOverrides` and inline styles
 * (a numeric `borderRadius` in `sx` is multiplied by the base). `@/ui` components and `cssText`
 * strings import this constant directly because they may run under an external theme.
 */
export const GEOVIEW_SHAPE = {
  borderRadiusNone: '0',
  borderRadiusSm: '4px',
  borderRadiusMd: '6px', // Also sets the numeric MUI base `theme.shape.borderRadius`; must stay a px value
  borderRadiusLg: '8px',
  borderRadiusFull: '9999px',
} as const;

/** Heading typography styles with bold weight */
export const headingStyles = {
  fontFamily: font,
  fontWeight: 700,
};

/**
 * Visually hidden style pattern for screen reader-only content.
 *
 * This CSS pattern hides content visually while keeping it accessible
 * to screen readers, following WCAG best practices.
 *
 * @see https://www.w3.org/WAI/WCAG21/Techniques/css/C7
 */
export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  // eslint-disable-next-line no-restricted-syntax -- shared constant has no theme; zero is unit-independent
  padding: 0,
  // eslint-disable-next-line no-restricted-syntax -- preserve the WCAG visually-hidden clipping offset
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/**
 * Standard CSS properties for text truncation with ellipsis.
 *
 * Use this for non-Typography elements (Box, span, div, table cells) that need text truncation.
 * For Typography components, use the `noWrap` prop instead.
 *
 * Note: This constant does not include `display` or width constraints. For ellipsis to work:
 * - The element must have `display: block` or `display: inline-block` (or be a flex/grid item)
 * - The element must have a constrained width (`maxWidth`, `width`, or flex constraints)
 * - For inline elements like `<span>`, use `display: 'inline-block'` to preserve inline flow
 *
 * @example
 * ```tsx
 * // Block element with maxWidth
 * const styles = {
 *   truncatedText: {
 *     display: 'block',
 *     maxWidth: '200px',
 *     ...ellipsisOverflow,
 *   },
 * };
 *
 * // Inline element (span) with inline-block
 * <Box component="span" sx={{ display: 'inline-block', maxWidth: '300px', ...ellipsisOverflow }}>
 *   {longText}
 * </Box>
 *
 * // Flex item (width constraint from parent)
 * <Box sx={{ display: 'flex' }}>
 *   <Box sx={{ flex: 1, minWidth: 0, ...ellipsisOverflow }}>
 *     {longText}
 *   </Box>
 * </Box>
 * ```
 */
export const ellipsisOverflow = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/** Opacity values for interactive states (hover, selected, disabled, focus, activated) */
export const opacity = {
  hoverOpacity: 0.08,
  selectedOpacity: 0.16,
  disabledOpacity: 0.38,
  focusOpacity: 0.12,
  activatedOpacity: 0.24,
};

/** Default GeoView color palette with primary, secondary, and semantic colors */
export const geoViewColors: IGeoViewColors = {
  white: '#FFFFFF',

  bgColor: new GeoViewColorClass('#F1F2F5'),
  primary: new GeoViewColorClass('#515BA5'),
  // This design has no distinct secondary colour. `secondary` uses the same color as
  // primary deliberately so that `color="secondary"` stays consistent with the brand
  // rather than falling back to MUI's default.
  secondary: new GeoViewColorClass('#515BA5'),
  textColor: new GeoViewColorClass('#393939'),
  success: new GeoViewColorClass('#2e7d32'), // Same as info for now
  error: new GeoViewColorClass('#d32f2f'),
  warning: new GeoViewColorClass('#ed6c02'),
  info: new GeoViewColorClass('#2e7d32'), // Same as success for now
  grey: new GeoViewColorClass('#9e9e9e'),

  /** Default focus indicator for WCAG compliance (geo.ca theme baseline) */
  focusIndicator: {
    outline: '#000',
    halo: '#fff',
  },
};

/**
 * Generates compatibility font sizes using the owning theme's calibrated conversion.
 *
 * @param pxToRem - Calibrated theme conversion from design pixels to rem
 * @returns Named scale sizes and retained legacy multiplier entries
 */
export const getGeoViewFontSizes = (pxToRem: (size: number) => string): IGeoViewFontSizes => {
  const fontSizes: IGeoViewFontSizes = {
    xs: pxToRem(TYPOGRAPHY_SCALE.xs),
    sm: pxToRem(TYPOGRAPHY_SCALE.sm),
    default: pxToRem(TYPOGRAPHY_SCALE.default),
    md: pxToRem(TYPOGRAPHY_SCALE.md),
    lg: pxToRem(TYPOGRAPHY_SCALE.lg),
    xl: pxToRem(TYPOGRAPHY_SCALE.xl),
    xxl: pxToRem(TYPOGRAPHY_SCALE.xxl),
    display: pxToRem(TYPOGRAPHY_SCALE.display),
  };

  for (let multiplier = 0.2; multiplier <= 10; multiplier += 0.1) {
    const key = `${multiplier}x`;
    fontSizes[key] = pxToRem(TYPOGRAPHY_SCALE.default * defaultFontSize * multiplier);
  }
  return fontSizes;
};

/** @deprecated Uses an uncalibrated 16px root. Read the owning map's theme.typography instead. */
export const geoViewFontSizes = getGeoViewFontSizes((size: number): string => `${size / 16}rem`);
